<div class="pp">

  <!-- ── Header ──────────────────────────────────── -->
  <div class="pp__hero">
    <div class="pp__hero-inner">

     <div class="pp__logo">
  <img
    src="assets/images/fieldlogo.png"
    alt="KinexOps Logo"
    class="pp__logo-image"
  >

  <span class="pp__logo-name">
    <span class="pp__logo-kinex">Kinex</span><span class="pp__logo-ops">Ops</span>
  </span>
</div>

      <h1 class="pp__title">Privacy Policy</h1>

      <p class="pp__subtitle">
        Your privacy matters. This policy explains how KinexOps collects,
        uses, and protects your data.
      </p>

      <div class="pp__meta">
        <span class="pp__badge">Last updated: {{ lastUpdated }}</span>
        <span class="pp__badge pp__badge--teal">GDPR Compliant</span>
        <span class="pp__badge pp__badge--teal">NDPR Compliant</span>
      </div>

    </div>
  </div>

  <!-- ── Table of Contents ───────────────────────── -->
  <div class="pp__container">
    <div class="pp__toc">
      <h2 class="pp__toc-title">Contents</h2>
      <ol class="pp__toc-list">
        <li><a href="#who-we-are">Who We Are</a></li>
        <li><a href="#data-we-collect">Data We Collect</a></li>
        <li><a href="#location-data">Location and GPS Data</a></li>
        <li><a href="#camera-photos">Camera and Photo Data</a></li>
        <li><a href="#how-we-use">How We Use Your Data</a></li>
        <li><a href="#data-sharing">Data Sharing and Disclosure</a></li>
        <li><a href="#data-storage">Data Storage and Security</a></li>
        <li><a href="#data-retention">Data Retention</a></li>
        <li><a href="#your-rights">Your Rights</a></li>
        <li><a href="#children">Children's Privacy</a></li>
        <li><a href="#third-party">Third-Party Services</a></li>
        <li><a href="#changes">Changes to This Policy</a></li>
        <li><a href="#contact">Contact Us</a></li>
      </ol>
    </div>

    <!-- ── Sections ────────────────────────────────── -->
    <div class="pp__content">

      <!-- 1. Who We Are -->
      <section id="who-we-are" class="pp__section">
        <h2 class="pp__section-title">
          <span class="pp__section-num">1</span>
          Who We Are
        </h2>
        <p>
          <strong>{{ companyName }}</strong> operates the <strong>{{ appName }}</strong>
          field operations management platform, including the web dashboard available at
          <a [href]="websiteUrl" target="_blank" rel="noopener">{{ websiteUrl }}</a>
          and the KinexOps Mobile application available on Google Play.
        </p>
        <p>
          KinexOps is a Software-as-a-Service (SaaS) platform designed for companies
          operating in field-based industries including oil and gas, construction,
          facility management, and infrastructure services. The platform enables managers
          to create and assign jobs, and enables field workers to report their activities,
          locations, and safety compliance from mobile devices.
        </p>
        <p>
          We are the <strong>data controller</strong> for personal data collected through
          our platform. For data processed on behalf of your employer (the company that
          deployed KinexOps), your employer is the data controller and we act as a
          data processor.
        </p>
        <div class="pp__highlight">
          <strong>Registered for:</strong> Nigerian Data Protection Regulation (NDPR)
          compliance administered by the National Information Technology Development
          Agency (NITDA), and General Data Protection Regulation (GDPR) for users
          in the European Economic Area.
        </div>
      </section>

      <!-- 2. Data We Collect -->
      <section id="data-we-collect" class="pp__section">
        <h2 class="pp__section-title">
          <span class="pp__section-num">2</span>
          Data We Collect
        </h2>
        <p>
          We collect different types of information depending on how you use KinexOps.
        </p>

        <h3 class="pp__subsection-title">Account and Profile Information</h3>
        <ul class="pp__list">
          <li>Full name and email address (collected when your account is created by your employer)</li>
          <li>Job title and role (Worker or Administrator)</li>
          <li>Company name and industry</li>
          <li>Company logo (uploaded by administrators)</li>
          <li>Encrypted password (we never store plain-text passwords)</li>
        </ul>

        <h3 class="pp__subsection-title">Job and Work Activity Data</h3>
        <ul class="pp__list">
          <li>Jobs assigned to you, including title, description, and location</li>
          <li>Work logs you submit: description of work done, hours spent, and timestamps</li>
          <li>Job check-in time (when you start a job) and check-out time (when you complete it)</li>
          <li>Digital signatures captured when completing jobs or accepting safety permits</li>
          <li>Permit to Work records, including safety checklists, gas test readings, and signatures</li>
          <li>Incident reports you file from the mobile application</li>
        </ul>

        <h3 class="pp__subsection-title">Technical and Device Data</h3>
        <ul class="pp__list">
          <li>Device type and operating system (Android or iOS)</li>
          <li>App version number</li>
          <li>Authentication tokens stored locally on your device</li>
          <li>Connection status (online or offline) for synchronisation purposes</li>
        </ul>
      </section>

      <!-- 3. Location Data -->
      <section id="location-data" class="pp__section">
        <h2 class="pp__section-title">
          <span class="pp__section-num">3</span>
          Location and GPS Data
        </h2>

        <div class="pp__sensitive-banner">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M12 2C8.1 2 5 5.1 5 9C5 14.2 12 22 12 22S19 14.2 19 9C19 5.1 15.9 2 12 2Z"
                  stroke="currentColor" stroke-width="2"/>
            <circle cx="12" cy="9" r="2.5" stroke="currentColor" stroke-width="2"/>
          </svg>
          <div>
            <strong>Sensitive Data Category — Device Location</strong>
            <p>
              KinexOps collects your precise GPS location while you are using the app
              on an active work shift. This is a core feature of the platform and is
              used exclusively for legitimate field operations management purposes.
            </p>
          </div>
        </div>

        <h3 class="pp__subsection-title">What Location Data We Collect</h3>
        <ul class="pp__list">
          <li>
            <strong>Real-time position</strong> — your current GPS latitude and longitude,
            captured approximately every 30 seconds while location tracking is active.
            This is transmitted to the KinexOps server and displayed on the Live Map
            for your company's administrators.
          </li>
          <li>
            <strong>Route history</strong> — a timestamped record of GPS coordinates
            captured during your work shift. This is stored and used to generate movement
            replay reports for operational and safety review.
          </li>
          <li>
            <strong>Job site coordinates</strong> — the GPS coordinates of job locations
            set by administrators when creating jobs. These are used to display job sites
            on maps and provide navigation assistance to field workers.
          </li>
          <li>
            <strong>Work log location</strong> — the GPS coordinates at the moment you
            submit a work log, captured to verify on-site presence.
          </li>
        </ul>

        <h3 class="pp__subsection-title">Why We Collect Location Data</h3>
        <ul class="pp__list">
          <li>To provide real-time visibility of field worker positions to authorised managers</li>
          <li>To verify that work is being performed at the correct job site</li>
          <li>For worker safety — to locate a worker in the event of an emergency or incident</li>
          <li>To provide navigation assistance to reach job sites</li>
          <li>To generate operational efficiency reports for your employer</li>
          <li>To support safety investigations and compliance audits</li>
        </ul>

        <h3 class="pp__subsection-title">Legal Basis for Processing Location Data</h3>
        <p>
          Location data is processed on the basis of <strong>legitimate interests</strong>
          of your employer to manage field operations safely and efficiently, and for
          <strong>contractual necessity</strong> as location-based features are core
          to the service your employer has subscribed to.
        </p>
        <p>
          Where required by law, your employer must inform you that location tracking
          is active as a condition of your employment or engagement. KinexOps requires
          subscribing companies to ensure workers are notified before location tracking begins.
        </p>

        <h3 class="pp__subsection-title">When Location is NOT Collected</h3>
        <ul class="pp__list">
          <li>When you are not logged in to the KinexOps Mobile app</li>
          <li>When location permissions have been denied on your device</li>
          <li>Location tracking does not continue when the app is completely closed
              (background tracking is limited to active work sessions only)</li>
        </ul>

        <h3 class="pp__subsection-title">Your Location Data Controls</h3>
        <ul class="pp__list">
          <li>You can revoke location permission at any time through your device settings
              (Settings → Apps → KinexOps → Permissions → Location)</li>
          <li>Revoking location permission will prevent the real-time tracking feature
              from functioning but will not prevent you from using other app features</li>
          <li>You may request deletion of your location history by contacting your
              company administrator or us directly at {{ contactEmail }}</li>
        </ul>
      </section>

      <!-- 4. Camera and Photos -->
      <section id="camera-photos" class="pp__section">
        <h2 class="pp__section-title">
          <span class="pp__section-num">4</span>
          Camera and Photo Data
        </h2>

        <div class="pp__sensitive-banner">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M23 19C23 19.5 22.8 20 22.4 20.4C22 20.8 21.5 21 21 21H3C2.5 21 2 20.8 1.6 20.4C1.2 20 1 19.5 1 19V8C1 7.5 1.2 7 1.6 6.6C2 6.2 2.5 6 3 6H7L9 3H15L17 6H21C21.5 6 22 6.2 22.4 6.6C22.8 7 23 7.5 23 8V19Z"
                  stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="12" cy="13" r="4" stroke="currentColor" stroke-width="2"/>
          </svg>
          <div>
            <strong>Sensitive Data Category — Camera and Photos</strong>
            <p>
              KinexOps requests camera permission to allow field workers to attach
              photo evidence to work logs and incident reports. Photos are uploaded
              to secure company-specific storage.
            </p>
          </div>
        </div>

        <h3 class="pp__subsection-title">What Camera and Photo Access Is Used For</h3>
        <ul class="pp__list">
          <li>
            <strong>Work log photos</strong> — workers may attach photos taken with
            the device camera to document completed work. Examples: equipment inspected,
            repairs made, site conditions observed.
          </li>
          <li>
            <strong>Incident report photos</strong> — workers may attach photos to
            safety incident reports to document the scene, damage, or injury.
          </li>
          <li>
            <strong>Company logo upload</strong> — administrators may upload their
            company logo via the web dashboard. This uses standard browser file selection,
            not device camera access.
          </li>
        </ul>

        <h3 class="pp__subsection-title">What We Do NOT Use Camera Access For</h3>
        <ul class="pp__list">
          <li>We do not access your camera at any time other than when you actively
              choose to take a photo within the app</li>
          <li>We do not record video</li>
          <li>We do not access your device photo gallery without explicit permission</li>
          <li>We do not use photos for facial recognition or biometric analysis</li>
          <li>We do not share photos with any third parties except as described in
              Section 6 of this policy</li>
        </ul>

        <h3 class="pp__subsection-title">Photo Storage</h3>
        <p>
          Photos uploaded through KinexOps are stored on our secure servers and are
          accessible only to your company's authorised administrators and the worker
          who uploaded them. Photos are retained for the duration of your company's
          subscription and may be included in PDF compliance reports.
        </p>

        <h3 class="pp__subsection-title">Your Camera Controls</h3>
        <ul class="pp__list">
          <li>Camera permission can be revoked at any time through your device settings</li>
          <li>You can request deletion of specific photos by contacting your administrator</li>
        </ul>
      </section>

      <!-- 5. How We Use Your Data -->
      <section id="how-we-use" class="pp__section">
        <h2 class="pp__section-title">
          <span class="pp__section-num">5</span>
          How We Use Your Data
        </h2>

        <div class="pp__use-grid">
          <div class="pp__use-card">
            <div class="pp__use-icon">⚙️</div>
            <h4>Service Delivery</h4>
            <p>To provide the KinexOps platform, including job management, work logging, permit processing, and safety compliance tracking.</p>
          </div>
          <div class="pp__use-card">
            <div class="pp__use-icon">🛡️</div>
            <h4>Safety and Compliance</h4>
            <p>To generate NUPRC and OSHA compliance reports, track incident investigation, and maintain audit records for regulatory purposes.</p>
          </div>
          <div class="pp__use-card">
            <div class="pp__use-icon">📊</div>
            <h4>Operational Intelligence</h4>
            <p>To provide AI-powered insights about job delays, worker productivity, and scheduling patterns to help managers make better decisions.</p>
          </div>
          <div class="pp__use-card">
            <div class="pp__use-icon">🔔</div>
            <h4>Notifications</h4>
            <p>To send real-time alerts for high-severity incidents, subscription renewal reminders, and important system updates.</p>
          </div>
          <div class="pp__use-card">
            <div class="pp__use-icon">💳</div>
            <h4>Billing</h4>
            <p>To process subscription payments through Paystack and maintain billing history records for your company account.</p>
          </div>
          <div class="pp__use-card">
            <div class="pp__use-icon">🔧</div>
            <h4>Platform Improvement</h4>
            <p>To identify and fix technical issues, improve performance, and develop new features based on how the platform is used.</p>
          </div>
        </div>
      </section>

      <!-- 6. Data Sharing -->
      <section id="data-sharing" class="pp__section">
        <h2 class="pp__section-title">
          <span class="pp__section-num">6</span>
          Data Sharing and Disclosure
        </h2>
        <p>
          We do not sell, rent, or trade your personal data. We share data only in
          the following limited circumstances:
        </p>

        <h3 class="pp__subsection-title">With Your Employer</h3>
        <p>
          Your work activity data, location history, work logs, photos, and incident
          reports are accessible to authorised administrators within your company.
          This is the core function of the KinexOps platform and the reason your
          employer has subscribed to the service.
        </p>

        <h3 class="pp__subsection-title">With Service Providers</h3>
        <ul class="pp__list">
          <li>
            <strong>Paystack</strong> — payment processing. Your company's billing
            email is shared with Paystack to process subscription payments. Paystack
            is PCI DSS compliant and does not receive your individual personal data.
          </li>
          <li>
            <strong>Google Maps Platform</strong> — map display and location search
            during job creation. Coordinates are transmitted to Google to render
            map tiles and provide Places autocomplete. Google's privacy policy
            applies to this processing.
          </li>
          <li>
            <strong>Hosting provider</strong> — our API and data are hosted on
            secure cloud servers. The hosting provider has access to server
            infrastructure but not to your personal data in any meaningful way.
          </li>
        </ul>

        <h3 class="pp__subsection-title">Legal Requirements</h3>
        <p>
          We may disclose your data if required by law, court order, or regulatory
          authority, including NITDA in Nigeria, and only to the extent required.
        </p>

        <h3 class="pp__subsection-title">Business Transfer</h3>
        <p>
          If KinexOps Technologies is involved in a merger, acquisition, or asset sale,
          user data may be transferred. We will notify users before data is transferred
          and a different privacy policy applies.
        </p>
      </section>

      <!-- 7. Data Storage and Security -->
      <section id="data-storage" class="pp__section">
        <h2 class="pp__section-title">
          <span class="pp__section-num">7</span>
          Data Storage and Security
        </h2>

        <h3 class="pp__subsection-title">Where Data is Stored</h3>
        <p>
          Your data is stored on secure servers. The KinexOps Mobile application
          also stores a local copy of your data on your device using SQLite for
          offline functionality. This local data is protected by your device's
          security and does not leave your device except through the KinexOps
          synchronisation process.
        </p>

        <h3 class="pp__subsection-title">Security Measures</h3>
        <ul class="pp__list">
          <li>All data is transmitted using HTTPS/TLS encryption</li>
          <li>Passwords are hashed using bcrypt — we never store plain-text passwords</li>
          <li>Authentication uses JWT tokens with expiry</li>
          <li>Permit to Work documents are protected by SHA-256 tamper-proof hashes</li>
          <li>Access to data is restricted by role — workers only see their own jobs;
              administrators only see their company's data</li>
          <li>Payment data is handled entirely by Paystack and we do not store
              card numbers or payment credentials</li>
        </ul>

        <h3 class="pp__subsection-title">Data Breach Response</h3>
        <p>
          In the event of a personal data breach that poses a risk to your rights and
          freedoms, we will notify affected users and relevant regulatory authorities
          (NITDA and/or relevant EU supervisory authorities) within 72 hours of
          becoming aware of the breach.
        </p>
      </section>

      <!-- 8. Data Retention -->
      <section id="data-retention" class="pp__section">
        <h2 class="pp__section-title">
          <span class="pp__section-num">8</span>
          Data Retention
        </h2>

        <div class="pp__table-wrap">
          <table class="pp__table">
            <thead>
              <tr>
                <th>Data Type</th>
                <th>Retention Period</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Account information</td>
                <td>Duration of subscription + 90 days after cancellation</td>
                <td>Service provision and billing disputes</td>
              </tr>
              <tr>
                <td>Work logs and job records</td>
                <td>Duration of subscription</td>
                <td>Operational records and compliance reporting</td>
              </tr>
              <tr>
                <td>GPS location history</td>
                <td>12 months from capture date</td>
                <td>Route replay, safety investigations, compliance</td>
              </tr>
              <tr>
                <td>Incident reports</td>
                <td>7 years from incident date</td>
                <td>NUPRC and OSHA regulatory requirements</td>
              </tr>
              <tr>
                <td>Permit to Work records</td>
                <td>7 years from permit closure</td>
                <td>Safety compliance and regulatory audit requirements</td>
              </tr>
              <tr>
                <td>Digital signatures</td>
                <td>Same as parent record (permit or job)</td>
                <td>Legal validity of signed documents</td>
              </tr>
              <tr>
                <td>Photos</td>
                <td>Duration of subscription</td>
                <td>Evidence for work logs and incident reports</td>
              </tr>
              <tr>
                <td>Payment history</td>
                <td>7 years from transaction date</td>
                <td>Tax and financial regulatory requirements</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- 9. Your Rights -->
      <section id="your-rights" class="pp__section">
        <h2 class="pp__section-title">
          <span class="pp__section-num">9</span>
          Your Rights
        </h2>
        <p>
          Depending on your location, you may have the following rights regarding your personal data:
        </p>

        <div class="pp__rights-grid">
          <div class="pp__right">
            <div class="pp__right-icon">👁</div>
            <h4>Right of Access</h4>
            <p>Request a copy of the personal data we hold about you.</p>
          </div>
          <div class="pp__right">
            <div class="pp__right-icon">✏️</div>
            <h4>Right to Rectification</h4>
            <p>Request correction of inaccurate personal data held about you.</p>
          </div>
          <div class="pp__right">
            <div class="pp__right-icon">🗑️</div>
            <h4>Right to Erasure</h4>
            <p>Request deletion of your personal data, subject to legal retention requirements.</p>
          </div>
          <div class="pp__right">
            <div class="pp__right-icon">⏸</div>
            <h4>Right to Restriction</h4>
            <p>Request that we limit how we process your personal data in certain circumstances.</p>
          </div>
          <div class="pp__right">
            <div class="pp__right-icon">📦</div>
            <h4>Right to Portability</h4>
            <p>Request your personal data in a structured, commonly used, machine-readable format.</p>
          </div>
          <div class="pp__right">
            <div class="pp__right-icon">🚫</div>
            <h4>Right to Object</h4>
            <p>Object to processing of your personal data based on legitimate interests.</p>
          </div>
        </div>

        <div class="pp__highlight pp__highlight--blue">
          <strong>How to exercise your rights:</strong> Contact us at
          <a href="mailto:{{ contactEmail }}">{{ contactEmail }}</a>.
          We will respond within 30 days. Note that some rights may be limited where
          data is processed on behalf of your employer — in those cases, please also
          contact your employer directly.
        </div>

        <p>
          If you are in Nigeria and are not satisfied with our response, you may lodge
          a complaint with <strong>NITDA</strong> at <a href="https://nitda.gov.ng"
          target="_blank" rel="noopener">nitda.gov.ng</a>. If you are in the EU/EEA,
          you may contact your local supervisory authority.
        </p>
      </section>

      <!-- 10. Children's Privacy -->
      <section id="children" class="pp__section">
        <h2 class="pp__section-title">
          <span class="pp__section-num">10</span>
          Children's Privacy
        </h2>
        <p>
          KinexOps is a professional enterprise platform designed exclusively for use
          by adults in employment or contractual field work arrangements. We do not
          knowingly collect personal data from anyone under the age of 18.
        </p>
        <p>
          If you believe a person under 18 has provided personal data through our
          platform, please contact us immediately at
          <a href="mailto:{{ contactEmail }}">{{ contactEmail }}</a>
          and we will take steps to delete that information promptly.
        </p>
      </section>

      <!-- 11. Third-Party Services -->
      <section id="third-party" class="pp__section">
        <h2 class="pp__section-title">
          <span class="pp__section-num">11</span>
          Third-Party Services
        </h2>
        <p>
          KinexOps integrates with the following third-party services. Each has
          its own privacy policy which we encourage you to review:
        </p>

        <div class="pp__third-party-list">
          <div class="pp__third-party">
            <h4>Google Maps Platform</h4>
            <p>Used for job site location display, route visualisation, and Places
               autocomplete. Location data is processed by Google in accordance with
               their <a href="https://policies.google.com/privacy" target="_blank"
               rel="noopener">Privacy Policy</a>.</p>
          </div>
          <div class="pp__third-party">
            <h4>Paystack</h4>
            <p>Used for subscription payment processing for companies in Nigeria.
               Financial data is handled by Paystack in accordance with their
               <a href="https://paystack.com/privacy" target="_blank"
               rel="noopener">Privacy Policy</a>. Paystack is PCI DSS certified.</p>
          </div>
          <div class="pp__third-party">
            <h4>Google Play</h4>
            <p>The KinexOps Mobile app is distributed through Google Play. Google
               collects certain information about app installations and usage. See
               <a href="https://policies.google.com/privacy" target="_blank"
               rel="noopener">Google's Privacy Policy</a>.</p>
          </div>
        </div>
      </section>

      <!-- 12. Changes -->
      <section id="changes" class="pp__section">
        <h2 class="pp__section-title">
          <span class="pp__section-num">12</span>
          Changes to This Policy
        </h2>
        <p>
          We may update this Privacy Policy from time to time to reflect changes in
          our practices, legal requirements, or the features we offer. When we make
          material changes, we will:
        </p>
        <ul class="pp__list">
          <li>Update the "Last updated" date at the top of this page</li>
          <li>Send an email notification to all registered administrator accounts</li>
          <li>Display an in-app notification on the KinexOps dashboard</li>
        </ul>
        <p>
          Your continued use of KinexOps after the effective date of any changes
          constitutes your acceptance of the updated policy. If you do not agree
          with the changes, you must stop using the platform and contact your
          company administrator.
        </p>
      </section>

      <!-- 13. Contact -->
      <section id="contact" class="pp__section">
        <h2 class="pp__section-title">
          <span class="pp__section-num">13</span>
          Contact Us
        </h2>
        <p>
          If you have any questions about this Privacy Policy, wish to exercise your
          data rights, or have concerns about how your data is being handled, please
          contact us:
        </p>

        <div class="pp__contact-card">
          <div class="pp__contact-item">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <polyline points="22,6 12,13 2,6"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <div>
              <strong>Email</strong>
              <a href="mailto:{{ contactEmail }}">{{ contactEmail }}</a>
            </div>
          </div>
          <div class="pp__contact-item">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
              <line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" stroke-width="2"/>
              <path d="M12 2C12 2 8 7 8 12C8 17 12 22 12 22"
                    stroke="currentColor" stroke-width="2"/>
              <path d="M12 2C12 2 16 7 16 12C16 17 12 22 12 22"
                    stroke="currentColor" stroke-width="2"/>
            </svg>
            <div>
              <strong>Website</strong>
              <a [href]="websiteUrl" target="_blank" rel="noopener">{{ websiteUrl }}</a>
            </div>
          </div>
        </div>

        <div class="pp__highlight">
          We aim to respond to all privacy-related enquiries within
          <strong>5 business days</strong> and to all formal rights requests
          within <strong>30 calendar days</strong> as required by applicable law.
        </div>
      </section>

    </div>
  </div>

  <!-- ── Footer ──────────────────────────────────── -->
  <div class="pp__footer">
    <p>© {{ appName }} {{ lastUpdated }}. All rights reserved.</p>
    <p>This policy applies to the KinexOps web dashboard and KinexOps Mobile (Android).</p>
    <a routerLink="/contact">Contact Us</a>
  </div>

</div>